import { IBreadcrumb } from '@/components/types'
import { RouteRecordRaw } from 'vue-router'
import localCache from '@/utils/cache'

export interface MenuItem {
  id?: string | number
  name?: string
  icon?: string
  uri?: string
  path?: string
  value?: string
  children?: unknown
  permissions?: unknown
  [key: string]: unknown
}

interface RouteCandidate {
  menu: MenuItem
  route: RouteRecordRaw
}

let firstMenu: MenuItem | null = null

const isMenuItem = (value: unknown): value is MenuItem =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

export const normalizeMenuItems = (menus: unknown): MenuItem[] =>
  Array.isArray(menus) ? menus.filter(isMenuItem) : []

export const getMenuChildren = (menu: MenuItem): MenuItem[] =>
  normalizeMenuItems(menu.children)

const getMenuPath = (menu: MenuItem) =>
  typeof menu.uri === 'string' ? menu.uri.trim() : ''

const getRoleNames = () => {
  const userInfo = localCache.getCache('userInfo')
  const roles = Array.isArray(userInfo?.roles) ? userInfo.roles : []

  return new Set(
    roles
      .map((role: any) =>
        typeof role?.roleName === 'string' ? role.roleName : ''
      )
      .filter(Boolean)
  )
}

const hasServiceDashboardRole = () => {
  const roleNames = getRoleNames()
  return ['ROLE_SHM', 'ROLE_SHG', 'ROLE_SHA'].some((roleName) =>
    roleNames.has(roleName)
  )
}

export function mapMenusToRoutes(userMenus: unknown): RouteRecordRaw[] {
  const allRoutes = new Map<string, RouteRecordRaw>()
  const routeFiles = require.context('../router/main', true, /\.ts/)

  routeFiles.keys().forEach((key) => {
    const route = require('../router/main' + key.split('.')[1]).default
    if (route?.path) {
      allRoutes.set(route.path, route)
    }
  })

  const candidates: RouteCandidate[] = []
  const visitedRoutePaths = new Set<string>()

  const collectRoutes = (menus: unknown) => {
    for (const menu of normalizeMenuItems(menus)) {
      const uri = getMenuPath(menu)
      const routePath =
        typeof menu.path === 'string' && menu.path.trim()
          ? menu.path.trim()
          : uri
      const route = routePath ? allRoutes.get(routePath) : undefined

      if (uri && route && !visitedRoutePaths.has(route.path)) {
        candidates.push({ menu, route })
        visitedRoutePaths.add(route.path)
      }

      collectRoutes(menu.children)
    }
  }

  collectRoutes(userMenus)

  const preferredPath = hasServiceDashboardRole()
    ? '/main/serviceDashboard'
    : '/main/noticeRecord'
  firstMenu =
    candidates.find(({ menu }) => getMenuPath(menu) === preferredPath)?.menu ||
    candidates.find(({ menu }) => getMenuPath(menu) === '/main/noticeRecord')
      ?.menu ||
    candidates[0]?.menu ||
    null

  return candidates.map(({ route }) => route)
}

export function getFirstMenuPath(): string | null {
  const path = firstMenu ? getMenuPath(firstMenu) : ''
  return path && path !== '/main' ? path : null
}

export function pathMapToMenu(
  userMenus: unknown,
  path: string
): MenuItem | null {
  const findMenu = (menus: unknown): MenuItem | null => {
    for (const menu of normalizeMenuItems(menus)) {
      const uri = getMenuPath(menu)
      if (uri === path) return menu

      const matchedChild = findMenu(menu.children)
      if (matchedChild) return matchedChild
    }

    return null
  }

  return findMenu(userMenus)
}

export function pathMapToBreadcrumbs(
  userMenus: unknown,
  path: string
): IBreadcrumb[] {
  const findBreadcrumbs = (
    menus: unknown,
    parents: IBreadcrumb[] = []
  ): IBreadcrumb[] => {
    for (const menu of normalizeMenuItems(menus)) {
      const name = typeof menu.name === 'string' ? menu.name : ''
      const uri = getMenuPath(menu)
      const current = name
        ? [...parents, { name, ...(uri ? { path: uri } : {}) }]
        : parents

      if (uri === path) return current

      const childBreadcrumbs = findBreadcrumbs(menu.children, current)
      if (childBreadcrumbs.length > 0) return childBreadcrumbs
    }

    return []
  }

  return findBreadcrumbs(userMenus)
}

export function mapMenusToPermissions(userMenus: unknown): string[] {
  const permissions = new Set<string>()

  const collectPermissions = (menus: unknown) => {
    for (const menu of normalizeMenuItems(menus)) {
      if (typeof menu.value === 'string' && menu.value.trim()) {
        permissions.add(menu.value)
      }
      collectPermissions(menu.children)
    }
  }

  collectPermissions(userMenus)
  return Array.from(permissions)
}

export function menuMapLeafKeys(menuList: unknown): Array<string | number> {
  const leafKeys: Array<string | number> = []

  const collectLeafKeys = (menus: unknown) => {
    for (const menu of normalizeMenuItems(menus)) {
      const permissions = normalizeMenuItems(menu.permissions)
      if (permissions.length > 0) {
        collectLeafKeys(permissions)
      } else if (typeof menu.id === 'string' || typeof menu.id === 'number') {
        leafKeys.push(menu.id)
      }
    }
  }

  collectLeafKeys(menuList)
  return leafKeys
}
